import { useMemo } from 'react';
import {
    Edit,
    SimpleShowLayout,
    SimpleForm,
    TextField,
    TextInput,
    required,
    NumberInput,
    SelectInput,
    NumberField,
    BooleanInput,
    number,
    minValue,
    SelectField,
    maxValue,
    maxLength,
    useRecordContext,
    FunctionField,
    Show,
    TopToolbar,
    usePermissions,
    useTranslate,
} from 'react-admin';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import { errorLevelChoices, errorTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { OnlyBulkDeleteActionButton } from '@/components/global/buttons/bulkButtons.jsx';
import { CustomBooleanField } from '@/components/global/fields/CustomBooleanField.jsx';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { useDrawer } from '@/composables/useDrawer.js';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { ChangeHistory } from '@/components/global/history/ChangeHistory.jsx';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';

const getFilters = (isMobile, translate) => [
    <SearchBar key='filter_search' source='value' label={translate('common.search')} alwaysOn />,
    <TextInput key='filter_code' source='code_like' label={translate('resources.contractor.fields.code')} alwaysOn={isMobile} />,
    <TextInput key='filter_name' source='name_like' label={translate('common.name')} alwaysOn={isMobile} />,
    <CustomSelectArrayInput
        key='filter_level'
        source='level_in'
        label={translate('resources.deviceError.labels.level')}
        choices={errorLevelChoices}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
        emptyText={'Wszystkie'}
    />,
    <CustomSelectArrayInput
        key='filter_type'
        source='type_in'
        label={translate('resources.software.fields.type')}
        choices={errorTypeChoices(translate)}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
        emptyText={'Wszystkie'}
    />,
];

export const DeviceErrorList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();

    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    return (
        <CustomList filters={filters} sort={{ field: 'code', order: 'ASC' }} actions={<ListActions />} addLastColumnShadow={true}>
            <CustomDatagridConfigurable bulkActionButtons={permissions.isAdmin ? <OnlyBulkDeleteActionButton /> : false} rowClick={false}>
                <NumberField source='code' label={translate('resources.contractor.fields.code')} />
                <TextField source='name' label={translate('resources.deviceError.labels.codeName')} />
                <SelectField source='level' label={translate('resources.deviceError.labels.level')} choices={errorLevelChoices} />
                <TextField source='title' label={translate('resources.deviceError.labels.friendlyName')} />
                <SelectField source='type' label={translate('resources.software.fields.type')} choices={errorTypeChoices(translate)} />
                <CustomBooleanField source='ticketCreate' label={translate('resources.deviceError.labels.ticketCreate')} />
                <FunctionField label={translate('common.options')} render={record => <MoreActionsPopover errorId={record.id} />} />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const DeviceErrorCreate = ({ handleClose }) => {
    const translate = useTranslate();
    return (
        <DeviceErrorDrawer handleClose={handleClose} title={translate('resources.deviceError.labels.newError')}>
            <CustomCreate handleClose={handleClose}>
                <DeviceErrorForm />
            </CustomCreate>
        </DeviceErrorDrawer>
    );
};

export const DeviceErrorShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    return (
        <DeviceErrorDrawer
            handleClose={handleClose}
            title={translate('resources.deviceError.labels.detailsTitle')}
            after={<ChangeHistory resource='device-error' recordId={elementId} />}
        >
            <Show id={elementId} actions={false}>
                <SimpleShowLayout>
                    <Box className='details-sample-list'>
                        <SimpleShowLayout>
                            <NumberField source='code' label={translate('resources.contractor.fields.code')} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <TextField source='name' label={translate('resources.deviceError.labels.codeName')} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <SelectField source='level' label={translate('resources.deviceError.labels.level')} choices={errorLevelChoices} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <TextField source='title' label={translate('resources.deviceError.labels.friendlyName')} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <SelectField source='type' label={translate('resources.software.fields.type')} choices={errorTypeChoices(translate)} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <TextField source='solutionTip' label={translate('resources.deviceError.labels.suggestions')} />
                        </SimpleShowLayout>
                        <SimpleShowLayout>
                            <CustomBooleanField source='ticketCreate' label={translate('resources.deviceError.labels.ticketCreate')} />
                        </SimpleShowLayout>
                    </Box>
                </SimpleShowLayout>
            </Show>
        </DeviceErrorDrawer>
    );
};

export const DeviceErrorEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <DeviceErrorDrawer handleClose={handleClose} title={translate('resources.deviceError.labels.editErrorTitle')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <DeviceErrorForm />
            </Edit>
        </DeviceErrorDrawer>
    );
};

const DeviceErrorForm = () => {
    const translate = useTranslate();
    return (
        <SimpleForm toolbar={<EditToolbar />}>
            <NumberInput source='code' validate={[required(translate('common.validation.required')), number(), minValue(0), maxValue(999999)]} fullWidth />
            <TextInput
                source='name'
                label={translate('resources.deviceError.labels.codeName')}
                validate={[required(translate('common.validation.required')), maxLength(60, translate('common.validation.maxLength', { count: 60 }))]}
                fullWidth
            />
            <SelectInput
                source='level'
                label={translate('resources.deviceError.labels.level')}
                choices={errorLevelChoices}
                defaultValue={3}
                validate={[required(translate('common.validation.required'))]}
                emptyText={'Wszystkie'}
                fullWidth
            />
            <TextInput
                source='title'
                label={translate('resources.deviceError.labels.friendlyName')}
                validate={[required(translate('common.validation.required')), maxLength(255, translate('common.validation.maxLength', { count: 255 }))]}
                fullWidth
            />
            <SelectInput
                source='type'
                label={translate('resources.software.fields.type')}
                choices={errorTypeChoices(translate)}
                defaultValue='mechanicalFailure'
                validate={[required(translate('common.validation.required'))]}
                emptyText={'Wszystkie'}
                fullWidth
            />
            <TextInput source='solutionTip' label={translate('resources.deviceError.labels.suggestions')} multiline fullWidth />
            <BooleanInput size='small' source='ticketCreate' label={translate('resources.deviceError.labels.ticketCreate')} />
        </SimpleForm>
    );
};

const MoreActionsPopover = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const { isLoading, permissions } = usePermissions();
    if (!record || isLoading) return null;

    return (
        <CustomMorePopover record={record}>
            <CustomShowModalButton onOpen={() => openDrawer(record.id, 'device-error-show')} label={translate('resources.deviceError.labels.showDetails')} />
            {permissions.isAdmin && (
                <CustomEditModalButton onOpen={() => openDrawer(record.id, 'device-error-edit')} label={translate('resources.deviceError.labels.editError')} />
            )}
        </CustomMorePopover>
    );
};

const ListActions = () => {
    const translate = useTranslate();
    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            <CustomToolbarActionsPopover>
                <CustomCreateButton label={translate('resources.deviceError.labels.addNewError')} />
                <CustomExportButton />
            </CustomToolbarActionsPopover>
        </TopToolbar>
    );
};

const DeviceErrorDrawer = ({ handleClose, title, children, after = null }) => (
    <Box>
        <Box className='popup-wrapper popup-wrapper-top'>
            <Box className='popup-box popup-box--header'>
                <Typography variant='h4'>{title}</Typography>
                <Box>
                    <CustomCloseButton onClose={handleClose} />
                </Box>
            </Box>
        </Box>
        <Box className='popup-wrapper border-bottom'>
            <Box className='popup-box'>{children}</Box>
        </Box>
        {after}
    </Box>
);

DeviceErrorCreate.propTypes = {
    handleClose: PropTypes.func,
};

DeviceErrorShow.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

DeviceErrorEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.string,
};

DeviceErrorDrawer.propTypes = {
    handleClose: PropTypes.func,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    after: PropTypes.node,
};
